// Environment configuration for Seattle Events Aggregator
// Handles different API endpoints for development vs production

const config = {
  development: {
    API_BASE_URL: 'http://localhost:8787/api',  // Local Wrangler dev server
    USE_MCP_SERVICE: true,  // Use MCP simulation for local development
    ENVIRONMENT: 'development',
    ENABLE_REAL_TIME_UPDATES: false,  // Disable real-time updates in dev
    DATA_REFRESH_INTERVAL: 60000,  // 1 minute for faster dev feedback
    ENABLE_DATA_QUALITY_MONITORING: true,
    BATCH_SIZE: 50,  // Smaller batches for development
    ENABLE_DUPLICATE_DETECTION: true,
    FALLBACK_TO_MOCK_DATA: true
  },
  production: {
    API_BASE_URL: process.env.REACT_APP_API_URL || '/api',  // Use Pages Functions proxy to avoid ad blocker issues
    USE_MCP_SERVICE: false,  // Use real Worker API via Pages Functions proxy
    ENVIRONMENT: 'production',
    ENABLE_REAL_TIME_UPDATES: true,  // Enable real-time updates in prod
    DATA_REFRESH_INTERVAL: 12 * 60 * 60 * 1000,  // 12 hours for production
    ENABLE_DATA_QUALITY_MONITORING: true,
    BATCH_SIZE: 100,  // Larger batches for production efficiency
    ENABLE_DUPLICATE_DETECTION: true,
    FALLBACK_TO_MOCK_DATA: false  // Disable mock data fallback in production
  }
};

// Detect environment with multiple checks
const getEnvironment = () => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Explicit production domains (Cloudflare Pages)
    if (hostname.includes('pages.dev') || hostname.includes('seattle-events-aggregator')) {
      return 'production';
    }
    
    // Development environments
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
      return 'development';
    }
    
    // Any other domain defaults to production
    return 'production';
  }
  
  // Fallback for server-side rendering or other contexts
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
};

const currentEnv = getEnvironment();
const currentConfig = config[currentEnv];

// Debug logging for environment detection
if (typeof window !== 'undefined') {
  console.log('🌍 Environment Detection:', {
    hostname: window.location.hostname,
    detectedEnv: currentEnv,
    fallbackToMockData: currentConfig.FALLBACK_TO_MOCK_DATA,
    apiBaseUrl: currentConfig.API_BASE_URL
  });
}

export const API_BASE_URL = currentConfig.API_BASE_URL;
export const USE_MCP_SERVICE = currentConfig.USE_MCP_SERVICE;
export const ENVIRONMENT = currentConfig.ENVIRONMENT;
export const ENABLE_REAL_TIME_UPDATES = currentConfig.ENABLE_REAL_TIME_UPDATES;
export const DATA_REFRESH_INTERVAL = currentConfig.DATA_REFRESH_INTERVAL;
export const ENABLE_DATA_QUALITY_MONITORING = currentConfig.ENABLE_DATA_QUALITY_MONITORING;
export const BATCH_SIZE = currentConfig.BATCH_SIZE;
export const ENABLE_DUPLICATE_DETECTION = currentConfig.ENABLE_DUPLICATE_DETECTION;
export const FALLBACK_TO_MOCK_DATA = currentConfig.FALLBACK_TO_MOCK_DATA;

// Helper functions for environment-specific behavior
export const isProduction = () => ENVIRONMENT === 'production';
export const isDevelopment = () => ENVIRONMENT === 'development';

// Data source configuration
export const getDataSourceConfig = () => ({
  refreshInterval: DATA_REFRESH_INTERVAL,
  batchSize: BATCH_SIZE,
  enableDuplicateDetection: ENABLE_DUPLICATE_DETECTION,
  enableRealTimeUpdates: ENABLE_REAL_TIME_UPDATES,
  fallbackToMockData: FALLBACK_TO_MOCK_DATA
});

// API configuration for scrapers
export const getScraperConfig = () => ({
  apiBaseUrl: API_BASE_URL,
  batchEndpoint: `${API_BASE_URL}/events/batch`,
  dataQualityEndpoint: `${API_BASE_URL}/events/data-quality`,
  authRequired: isProduction(),
  rateLimits: {
    requestsPerMinute: isProduction() ? 10 : 60,
    maxBatchSize: BATCH_SIZE
  }
});

export default currentConfig;